import { AlgoliaSearchManager } from "../utils/algolia-search-grants";
import { URLManager } from "../utils/grants-database/URLManager";
import { FilterManager } from "../utils/grants-database/FilterManager";
import { SortManager } from "../utils/grants-database/SortManager";
import { TabManager } from "../utils/grants-database/TabManager";

export function initGrantsDatabase() {
  const algoliaAppId = document.getElementById("algolia-app-id")?.value;
  const algoliaSearchKey = document.getElementById("algolia-search-key")?.value;
  const algoliaIndexName = document.getElementById("algolia-index-name")?.value;
  const algoliaInitialFilters =
    document.getElementById("algolia-initial-filters")?.value || null;
  const algoliaInitialSortByIndex =
    document.getElementById("algolia-initial-sort-by-index")?.value || null;

  const grantsTabs = JSON.parse(
    document.getElementById("grants-tabs-data")?.value || "[]",
  );

  if (!algoliaAppId || !algoliaSearchKey || !algoliaIndexName) {
    console.error("Algolia credentials missing. Cannot initialize search.");
    return;
  }

  const urlManager = new URLManager();
  const algoliaManager = new AlgoliaSearchManager(
    algoliaAppId,
    algoliaSearchKey,
    algoliaIndexName,
    algoliaInitialFilters,
    algoliaInitialSortByIndex || algoliaIndexName,
  );
  const filterManager = new FilterManager(
    algoliaManager,
    performSearchAndRender,
    urlManager,
    grantsTabs,
  );
  const sortManager = new SortManager(algoliaManager, performSearchAndRender);
  const tabManager = new TabManager(
    urlManager,
    algoliaManager,
    filterManager,
    grantsTabs,
    performSearchAndRender,
  );

  const searchInput = document.getElementById("grant-search");
  const searchResultsContainer = document.getElementById(
    "algolia-search-results",
  );
  const paginationContainer = document.getElementById("algolia-pagination");
  const resultsCountElement = document.getElementById("algolia-results-count");
  const searchAnnouncer = document.getElementById("search-announcements");
  const footnoteElement = document.getElementById("gdb-footnote");

  let previousTotalHits = -1; // For accessibility announcements

  // Event listener for search input
  if (searchInput) {
    let searchTimeout = null;
    searchInput.addEventListener("input", (e) => {
      const query = e.target.value.trim();
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(async () => {
        await performSearchAndRender(query, 0, true);
        let { tabSlug, year } = urlManager.parseGrantsURL();
        // Default to appropriate hashtag if no tab is specified in URL
        if (!tabSlug) {
          if (grantsTabs && grantsTabs.length > 1) {
            tabSlug = grantsTabs[0].slug;
          } else {
            // Single tab or no tabs - use featured-grants wrapper ID
            tabSlug = "featured-grants";
          }
        }
        // Get amount as index, not value
        const amountIndex =
          filterManager.grantAmountSelect?.selectedIndex > 0
            ? filterManager.grantAmountSelect.selectedIndex.toString()
            : "";
        urlManager.updateURL(tabSlug, query, year, amountIndex, true);
      }, 300);
    });

    // Prevent Enter key from submitting/reloading the page
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
      }
    });
  }

  // Function to perform search and render results
  async function performSearchAndRender(
    query,
    page = 0,
    shouldAnnounce = true,
  ) {
    const { hits, nbHits, nbPages } = await algoliaManager.performSearch(
      query,
      page,
    );
    renderResults(hits, nbHits, shouldAnnounce);
    renderPagination(nbPages, page);
    updateResultsCount(nbHits, hits.length);
  }

  // Initial search and render
  (async () => {
    const { searchTerm, amount } = urlManager.parseGrantsURL();
    // Set amount filter if present in URL (amount is index, not value)
    if (amount && filterManager.grantAmountSelect) {
      const amountIndex = parseInt(amount, 10);
      if (
        !isNaN(amountIndex) &&
        amountIndex >= 0 &&
        amountIndex < filterManager.grantAmountSelect.options.length
      ) {
        filterManager.grantAmountSelect.selectedIndex = amountIndex;
        const selectedValue = filterManager.grantAmountSelect.value;
        if (selectedValue) {
          algoliaManager.setAlgoliaPriceRangeFilter(selectedValue);
        }
      }
    }
    await performSearchAndRender(searchTerm, 0, true);
  })();

  function renderResults(hits, totalHits, shouldAnnounce) {
    if (!searchResultsContainer) return;

    searchResultsContainer.style.display = "block"; // Ensure the container is visible
    let announcementMessage = "";

    if (hits.length === 0) {
      searchResultsContainer.innerHTML = `
        <div class="py-8 text-center text-gray border-t-3 border-black">No grants found matching your search</div>
      `;
      if (shouldAnnounce && totalHits !== previousTotalHits) {
        announcementMessage = "No grants found matching your search.";
      }
    } else {
      const resultsHtml = hits
        .map((hit) => {
          // Get highlighted versions with fallbacks
          // Handle array case for organization_name
          const organizationNameHighlighted = Array.isArray(
            hit._highlightResult?.organization_name,
          )
            ? hit._highlightResult.organization_name[0]?.value
            : hit._highlightResult?.organization_name?.value;

          const organizationName =
            organizationNameHighlighted ||
            (Array.isArray(hit.organization_name)
              ? hit.organization_name[0]
              : hit.organization_name || "Unlisted Organization"); // fallback name
          const amount = hit.grant_amount
            ? "$" + parseInt(hit.grant_amount).toLocaleString()
            : " "; // no result message
          let date = hit.award_date || " "; // no result message
          if (typeof date === "number" && !isNaN(date)) {
            const d = new Date(date * 1000);
            date = d.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
            });
          }
          const purpose =
            hit._highlightResult?.title?.value || hit.title || " "; // no purpose specified
          const link = hit.organization_website;

          return `
          <div is="grant-table-row" class="md:grid-cols-4 gap-x-5 gap-y-1 md:gap-8 border-b-lightborder group before:bg-lightborder hover:before:scale-y-100 relative grid grid-cols-2 border-b-1 transition-colors before:absolute before:-z-1 before:size-full before:scale-y-0 before:transition-transform before:content-[''] before:origin-[var(--origin)]">
            ${
              link
                ? `<a href="${link}" class="text-sans-sm py-2.5 font-bold row-span-3 md:row-auto [&_em]:not-italic" target="_blank">${organizationName}<span class="whitespace-nowrap decoration-none">&nbsp;<svg class="inline-block" width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.18022 9.96831L9.2283 4.89553L3.81893 10.3049L3.07364 9.55961L8.48301 4.15024L3.3982 4.1863L4.38391 3.20059L10.1779 3.20059L10.1659 8.98261L9.18022 9.96831Z" fill="#333333"/></svg></span></a>`
                : `<div class="text-sans-sm py-2.5 font-bold row-span-3 md:row-auto flex flex-row items-center gap-1 [&_em]:not-italic">${organizationName}</div>`
            }
              <div class="text-sans-sm pt-2.5 md:py-2.5 col-start-2 md:col-auto [&_em]:not-italic">${purpose}</div>
              <div class="text-sans-sm pb-0 pt-0 md:py-2.5 col-start-2 md:col-auto">${date}</div>
              <div class="text-sans-sm pb-2.5 md:py-2.5 col-start-2 md:col-auto">${amount}</div>
              ${
                hit.short_search_description
                  ? `
                <div class="js-grant-info-modal-container relative flex items-center justify-end h-full">
                  <button class="js-info-button relative w-5 h-5 cursor-pointer" data-grant-id="${hit.objectID}" tabindex="0" type="button" aria-haspopup="dialog" aria-expanded="false" aria-label="More information about ${organizationName}">
                    <span class="js-info-icon-default absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-100 group-hover:opacity-0">
                      <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10.3203" r="9.5" stroke="#808080"/>
                        <path d="M9.41159 5.86564H10.5881V7.23064H9.41159V5.86564ZM9.43109 8.43314H10.5751V15.0566H9.43109V8.43314Z" fill="#808080"/>
                      </svg>
                    </span>
                    <span class="js-info-icon-hover absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                      <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10.3203" r="9.5" fill="#064D49" stroke="#064D49"/>
                        <path d="M9.41159 5.86564H10.5881V7.23064H9.41159V5.86564ZM9.43109 8.43314H10.5751V15.0566H9.43109V8.43314Z" fill="#F6F6F6"/>
                      </svg>
                    </span>
                  </button>
                  <div class="js-grant-info-modal-content" data-grant-id="${hit.objectID}" role="dialog" aria-modal="true" aria-labelledby="modal-title-${hit.objectID}" aria-hidden="true">
                    <h2 id="modal-title-${hit.objectID}" class="sr-only">Short Description for ${organizationName}</h2>
                    <div class="text-sans-sm">${hit.short_search_description}</div>
                    <button class="js-close-grant-info-modal absolute top-2 right-5 p-1 cursor-pointer" aria-label="Close information modal">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                  </div>
                </div>
              `
                  : ""
              }
            </div>
        `;
        })
        .join("");

      searchResultsContainer.innerHTML = `
        <div class="flex flex-col border-t-3 border-t-black">
          ${resultsHtml}
        </div>
      `;
      if (shouldAnnounce && totalHits !== previousTotalHits) {
        announcementMessage = `Found ${totalHits} grants.`;
      }
    }

    if (searchAnnouncer && announcementMessage) {
      searchAnnouncer.textContent = announcementMessage;
    }
    previousTotalHits = totalHits;

    // Add event listeners for info buttons and modals
    searchResultsContainer
      .querySelectorAll(".js-info-button")
      .forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          const grantId = button.dataset.grantId;
          const content = searchResultsContainer.querySelector(
            `.js-grant-info-modal-content[data-grant-id="${grantId}"]`,
          );

          if (content) {
            const isOpen = content.classList.contains("is-open");

            // Close all other open grant-info-modals
            searchResultsContainer
              .querySelectorAll(".js-grant-info-modal-content.is-open")
              .forEach((openContent) => {
                if (openContent !== content) {
                  openContent.classList.remove("is-open");
                  openContent.setAttribute("aria-hidden", "true");
                  const associatedButton = searchResultsContainer.querySelector(
                    `.js-info-button[data-grant-id="${openContent.dataset.grantId}"]`,
                  );
                  if (associatedButton) {
                    associatedButton.setAttribute("aria-expanded", "false");
                    if (window.innerWidth <= 768) {
                      // Only show on mobile
                      associatedButton.classList.remove("hide-element"); // Show the info button
                    }
                  }
                }
              });

            if (!isOpen) {
              content.classList.add("is-open");
              content.setAttribute("aria-hidden", "false");
              button.setAttribute("aria-expanded", "true");
              if (window.innerWidth <= 768) {
                // Only hide on mobile
                button.classList.add("hide-element"); // Hide the info button when modal opens
              }

              // Focus on the close button when opening
              const closeButton = content.querySelector(
                ".js-close-grant-info-modal",
              );
              if (closeButton) {
                closeButton.focus();
              }
            } else {
              content.classList.remove("is-open");
              content.setAttribute("aria-hidden", "true");
              button.setAttribute("aria-expanded", "false");
              if (window.innerWidth <= 768) {
                // Only show on mobile
                button.classList.remove("hide-element"); // Show the info button when modal closes
              }

              button.focus(); // Return focus to the info button
            }
          }
        });

        button.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            button.click();
          }
        });
      });

    searchResultsContainer
      .querySelectorAll(".js-close-grant-info-modal")
      .forEach((closeButton) => {
        closeButton.addEventListener("click", (e) => {
          const content = closeButton.closest(".js-grant-info-modal-content");
          if (content) {
            content.classList.remove("is-open");
            content.setAttribute("aria-hidden", "true");
            const associatedButton = searchResultsContainer.querySelector(
              `.js-info-button[data-grant-id="${content.dataset.grantId}"]`,
            );
            if (associatedButton) {
              associatedButton.setAttribute("aria-expanded", "false");
              if (window.innerWidth <= 768) {
                // Only show on mobile
                associatedButton.classList.remove("hide-element"); // Show the info button
              }
              associatedButton.focus();
            }
          }
        });

        closeButton.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            closeButton.click();
          }
        });
      });

    document.addEventListener("click", (e) => {
      searchResultsContainer
        .querySelectorAll(".js-grant-info-modal-content.is-open")
        .forEach((content) => {
          const button = searchResultsContainer.querySelector(
            `.js-info-button[data-grant-id="${content.dataset.grantId}"]`,
          );
          if (
            content &&
            !content.contains(e.target) &&
            (!button || !button.contains(e.target))
          ) {
            content.classList.remove("is-open");
            content.setAttribute("aria-hidden", "true");
            if (button) {
              button.setAttribute("aria-expanded", "false");
              if (window.innerWidth <= 768) {
                // Only show on mobile
                button.classList.remove("hide-element"); // Show the info button
              }
            }
          }
        });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        searchResultsContainer
          .querySelectorAll(".js-grant-info-modal-content.is-open")
          .forEach((content) => {
            content.classList.remove("is-open");
            content.setAttribute("aria-hidden", "true");
            const associatedButton = searchResultsContainer.querySelector(
              `.js-info-button[data-grant-id="${content.dataset.grantId}"]`,
            );
            if (associatedButton) {
              associatedButton.setAttribute("aria-expanded", "false");
              if (window.innerWidth <= 768) {
                // Only show on mobile
                associatedButton.classList.remove("hide-element"); // Show the info button
              }
              associatedButton.focus();
            }
          });
      }
    });
  }

  function renderPagination(totalPages, currentPage) {
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.style.display = "none";
      return;
    }

    paginationContainer.style.display = "block";

    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    let pagesHtml = "";
    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === currentPage ? "before:scale-y-100" : "";
      pagesHtml += `
        <li>
          <a class="text-sans-sm w-9 h-12 flex relative before:content-[''] before:absolute before:w-full before:h-[3px] before:top-0 before:left-0 before:scale-y-0 before:origin-top before:transition-all hover:before:scale-y-100 before:bg-black ${activeClass}"
             href="#" data-page="${i}" aria-label="Go to page ${i + 1}" ${i === currentPage ? 'aria-current="page"' : ""}>
            <span class="m-auto">${i + 1}</span>
          </a>
        </li>
      `;
    }

    const prevDisabled =
      currentPage === 0 ? "opacity-50 pointer-events-none" : "";
    const nextDisabled =
      currentPage === totalPages - 1 ? "opacity-50 pointer-events-none" : "";

    paginationContainer.innerHTML = `
      <div class="flex flex-col md:flex-row justify-between items-center md:items-start">
        <ul class="flex flex-row flex-nowrap">
          ${pagesHtml}
        </ul>
        <ul class="flex flex-nowrap">
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:rotate-90 border-r-1 border-lightborder ${prevDisabled}"
               href="#" data-page="${currentPage - 1}" id="algolia-prev" aria-label="Go to previous page">
              <span class="sr-only">Previous</span>
            </a>
          </li>
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:-rotate-90 ${nextDisabled}"
               href="#" data-page="${currentPage + 1}" id="algolia-next" aria-label="Go to next page">
              <span class="sr-only">Next</span>
            </a>
          </li>
        </ul>
      </div>
    `;

    paginationContainer.querySelectorAll("a[data-page]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page);
        if (page >= 0 && page < totalPages && page !== currentPage) {
          performSearchAndRender(algoliaManager.currentQuery, page);
        }
      });
    });
  }

  function updateResultsCount(total = 0, perPage = 0) {
    if (resultsCountElement) {
      resultsCountElement.textContent = `Showing ${perPage} of ${total}`;
      resultsCountElement.style.display = "block";
      updateFootnoteSpacing();
    }
  }

  function updateFootnoteSpacing() {
    if (!paginationContainer || !footnoteElement) {
      return;
    }

    const isPaginationVisible =
      window.getComputedStyle(paginationContainer).display !== "none";

    footnoteElement.style.marginTop = isPaginationVisible
      ? window.innerWidth <= 767
        ? "10px"
        : "0"
      : "20px";
  }
}
